import { Fragment, h, type ComponentChildren } from "preact";
import { isSafeExternalUrl } from "@gnu-taler/taler-util";

// Exchange terms are untrusted input. This deliberately small Markdown
// renderer creates Preact nodes only: it never interprets raw HTML or loads
// images, and it exposes only absolute HTTP(S) links to the host application.
const MAX_NESTING = 24;
export const MAX_SAFE_MARKDOWN_BYTES = 256 * 1024;

type InlineNode =
  | { kind: "text"; value: string }
  | { kind: "break" }
  | { kind: "code"; value: string }
  | { kind: "emphasis"; children: InlineNode[] }
  | { kind: "strong"; children: InlineNode[] }
  | { kind: "link"; url?: string; children: InlineNode[] };

type BlockNode =
  | { kind: "heading"; level: number; children: InlineNode[] }
  | { kind: "paragraph"; children: InlineNode[] }
  | { kind: "rule" }
  | { kind: "quote"; children: BlockNode[] }
  | { kind: "code"; value: string }
  | {
      kind: "list";
      ordered: boolean;
      start: number;
      items: BlockNode[][];
    };

type ListMarker = {
  indent: number;
  ordered: boolean;
  start: number;
  contentIndent: number;
  content: string;
};

function normalizeUrl(value: string): string | undefined {
  try {
    const url = new URL(value);
    return isSafeExternalUrl(url.href) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function appendText(nodes: InlineNode[], value: string): void {
  if (!value) return;
  const previous = nodes.at(-1);
  if (previous?.kind === "text") previous.value += value;
  else nodes.push({ kind: "text", value });
}

type InlineIndex = {
  nextSquareClose: Int32Array;
  nextAngleClose: Int32Array;
  parenthesisClose: Int32Array;
  nextMarker: Map<string, Int32Array>;
  backtickClose: Int32Array;
};

function escapedCharacters(text: string): Uint8Array {
  const escaped = new Uint8Array(text.length);
  let slashes = 0;
  for (let index = 0; index < text.length; index++) {
    escaped[index] = slashes % 2;
    slashes = text[index] === "\\" ? slashes + 1 : 0;
  }
  return escaped;
}

function nextCharacterIndex(
  text: string,
  escaped: Uint8Array,
  character: string,
): Int32Array {
  const result = new Int32Array(text.length + 1);
  let next = -1;
  result[text.length] = -1;
  for (let index = text.length - 1; index >= 0; index--) {
    if (text[index] === character && !escaped[index]) next = index;
    result[index] = next;
  }
  return result;
}

function nextMarkerIndex(
  text: string,
  escaped: Uint8Array,
  marker: string,
): Int32Array {
  const result = new Int32Array(text.length + 1);
  let next = -1;
  result[text.length] = -1;
  for (let index = text.length - 1; index >= 0; index--) {
    if (text.startsWith(marker, index) && !escaped[index]) next = index;
    result[index] = next;
  }
  return result;
}

function inlineIndex(text: string): InlineIndex {
  const escaped = escapedCharacters(text);
  const parenthesisClose = new Int32Array(text.length + 1);
  const backtickClose = new Int32Array(text.length + 1);
  parenthesisClose.fill(-1);
  backtickClose.fill(-1);
  const parentheses: number[] = [];
  const pendingBackticks = new Map<number, number>();
  for (let index = 0; index < text.length; index++) {
    if (escaped[index]) continue;
    if (text[index] === "(") parentheses.push(index);
    else if (text[index] === ")") {
      const opening = parentheses.pop();
      if (opening !== undefined) parenthesisClose[opening] = index;
    }
    if (text[index] !== "`") continue;
    let runLength = 1;
    while (text[index + runLength] === "`") runLength++;
    const opening = pendingBackticks.get(runLength);
    if (opening === undefined) pendingBackticks.set(runLength, index);
    else {
      backtickClose[opening] = index;
      pendingBackticks.delete(runLength);
    }
    index += runLength - 1;
  }
  const nextMarker = new Map<string, Int32Array>();
  for (const marker of ["*", "**", "***", "_", "__", "___"])
    nextMarker.set(marker, nextMarkerIndex(text, escaped, marker));
  return {
    nextSquareClose: nextCharacterIndex(text, escaped, "]"),
    nextAngleClose: nextCharacterIndex(text, escaped, ">"),
    parenthesisClose,
    nextMarker,
    backtickClose,
  };
}

function linkAt(
  text: string,
  start: number,
  index: InlineIndex,
): { label: string; destination: string; end: number } | undefined {
  const labelEnd = index.nextSquareClose[start + 1] ?? -1;
  if (labelEnd === -1 || text[labelEnd + 1] !== "(") return undefined;
  const destinationEnd = index.parenthesisClose[labelEnd + 1] ?? -1;
  if (destinationEnd === -1) return undefined;
  let destination = text.slice(labelEnd + 2, destinationEnd).trim();
  if (destination.startsWith("<") && destination.endsWith(">"))
    destination = destination.slice(1, -1);
  return {
    label: text.slice(start + 1, labelEnd),
    destination,
    end: destinationEnd + 1,
  };
}

function inlineNodes(text: string, depth = 0, allowLinks = true): InlineNode[] {
  if (depth >= MAX_NESTING) return [{ kind: "text", value: text }];
  const delimiters = inlineIndex(text);
  const nodes: InlineNode[] = [];
  let index = 0;
  while (index < text.length) {
    const character = text[index];
    if (character === "\n") {
      nodes.push({ kind: "break" });
      index++;
      continue;
    }
    if (character === "\\" && index + 1 < text.length) {
      const escaped = text[index + 1];
      if (/^[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]$/.test(escaped)) {
        appendText(nodes, escaped);
        index += 2;
        continue;
      }
    }
    if (character === "`") {
      let runLength = 1;
      while (text[index + runLength] === "`") runLength++;
      const end = delimiters.backtickClose[index] ?? -1;
      if (end !== -1) {
        let value = text
          .slice(index + runLength, end)
          .replace(/[\t\n ]+/g, " ");
        if (value.startsWith(" ") && value.endsWith(" ") && value.trim())
          value = value.slice(1, -1);
        nodes.push({ kind: "code", value });
        index = end + runLength;
        continue;
      }
    }
    if (text.startsWith("![", index)) {
      const image = linkAt(text, index + 1, delimiters);
      if (image) {
        nodes.push(...inlineNodes(image.label, depth + 1, false));
        index = image.end;
        continue;
      }
    }
    if (allowLinks && character === "[") {
      const link = linkAt(text, index, delimiters);
      if (link) {
        nodes.push({
          kind: "link",
          url: normalizeUrl(link.destination),
          children: inlineNodes(link.label, depth + 1, false),
        });
        index = link.end;
        continue;
      }
    }
    if (allowLinks && character === "<") {
      const end = delimiters.nextAngleClose[index + 1] ?? -1;
      if (end !== -1) {
        const candidate = text.slice(index + 1, end);
        const url = normalizeUrl(candidate);
        if (url) {
          nodes.push({
            kind: "link",
            url,
            children: inlineNodes(candidate, depth + 1, false),
          });
          index = end + 1;
          continue;
        }
      }
    }
    const combinedMarker = text.startsWith("***", index)
      ? "***"
      : text.startsWith("___", index)
        ? "___"
        : undefined;
    if (combinedMarker) {
      const end = delimiters.nextMarker.get(combinedMarker)?.[index + 3] ?? -1;
      if (end > index + 3) {
        nodes.push({
          kind: "strong",
          children: [
            {
              kind: "emphasis",
              children: inlineNodes(text.slice(index + 3, end), depth + 1),
            },
          ],
        });
        index = end + 3;
        continue;
      }
    }
    const strongMarker = text.startsWith("**", index)
      ? "**"
      : text.startsWith("__", index)
        ? "__"
        : undefined;
    if (strongMarker) {
      const end = delimiters.nextMarker.get(strongMarker)?.[index + 2] ?? -1;
      if (end > index + 2) {
        nodes.push({
          kind: "strong",
          children: inlineNodes(text.slice(index + 2, end), depth + 1),
        });
        index = end + 2;
        continue;
      }
    }
    if (character === "*" || character === "_") {
      const end = delimiters.nextMarker.get(character)?.[index + 1] ?? -1;
      if (end > index + 1) {
        nodes.push({
          kind: "emphasis",
          children: inlineNodes(text.slice(index + 1, end), depth + 1),
        });
        index = end + 1;
        continue;
      }
    }
    appendText(nodes, character);
    index++;
  }
  return nodes;
}

function listMarker(line: string): ListMarker | undefined {
  const match = line.match(/^( *)([-+*]|(\d+)[.)])(?:[\t ]+(.*))?$/);
  if (!match) return undefined;
  const indent = match[1].length;
  const marker = match[2];
  return {
    indent,
    ordered: !!match[3],
    start: match[3] ? Number(match[3]) : 1,
    contentIndent: indent + marker.length + 1,
    content: match[4] ?? "",
  };
}

function isThematicBreak(line: string): boolean {
  const compact = line.trim().replaceAll(/[\t ]/g, "");
  return (
    compact.length >= 3 &&
    (compact.split("").every((value) => value === "-") ||
      compact.split("").every((value) => value === "_") ||
      compact.split("").every((value) => value === "*"))
  );
}

function startsBlock(line: string): boolean {
  return (
    /^ {0,3}#{1,6}(?:[\t ]|$)/.test(line) ||
    /^ {0,3}(?:`{3,}|~{3,})/.test(line) ||
    /^ {0,3}>/.test(line) ||
    isThematicBreak(line) ||
    (listMarker(line)?.indent ?? 4) <= 3
  );
}

function paragraphText(lines: string[]): string {
  return lines
    .map((line, index) => {
      if (index === lines.length - 1) return line;
      if (/[\t ]{2,}$/.test(line)) return `${line.trimEnd()}\n`;
      if (line.endsWith("\\")) return `${line.slice(0, -1)}\n`;
      return `${line} `;
    })
    .join("");
}

function parseList(
  lines: string[],
  startIndex: number,
  depth: number,
): { block: BlockNode; nextIndex: number } {
  const first = listMarker(lines[startIndex]);
  if (!first) throw Error("list parser called without a list marker");
  const items: BlockNode[][] = [];
  let index = startIndex;
  while (index < lines.length) {
    const marker = listMarker(lines[index]);
    if (
      !marker ||
      marker.indent !== first.indent ||
      marker.ordered !== first.ordered
    )
      break;
    const itemLines = [marker.content];
    index++;
    while (index < lines.length) {
      const line = lines[index];
      if (!line.trim()) {
        itemLines.push("");
        index++;
        continue;
      }
      const nextMarker = listMarker(line);
      if (
        nextMarker?.indent === first.indent &&
        nextMarker.ordered === first.ordered
      )
        break;
      const leading = line.match(/^ */)?.[0].length ?? 0;
      if (leading <= first.indent) break;
      itemLines.push(line.slice(Math.min(leading, marker.contentIndent)));
      index++;
    }
    while (!itemLines.at(-1)?.trim()) itemLines.pop();
    items.push(blockNodes(itemLines, depth + 1));
  }
  return {
    block: {
      kind: "list",
      ordered: first.ordered,
      start: first.start,
      items,
    },
    nextIndex: index,
  };
}

function blockNodes(sourceLines: string[], depth = 0): BlockNode[] {
  if (depth >= MAX_NESTING)
    return [
      {
        kind: "paragraph",
        children: inlineNodes(sourceLines.join("\n"), depth),
      },
    ];
  const lines = sourceLines.map((line) => line.replace(/\r$/, ""));
  const blocks: BlockNode[] = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index++;
      continue;
    }
    const fence = line.match(/^ {0,3}(`{3,}|~{3,}).*$/);
    if (fence) {
      const marker = fence[1];
      const value: string[] = [];
      index++;
      const closing = new RegExp(
        `^ {0,3}${marker[0]}{${marker.length},}[\\t ]*$`,
      );
      while (index < lines.length && !closing.test(lines[index])) {
        value.push(lines[index]);
        index++;
      }
      if (index < lines.length) index++;
      blocks.push({ kind: "code", value: value.join("\n") });
      continue;
    }
    const heading = line.match(/^ {0,3}(#{1,6})(?:[\t ]+(.*?)[\t ]*|[\t ]*)$/);
    if (heading) {
      blocks.push({
        kind: "heading",
        level: heading[1].length,
        children: inlineNodes((heading[2] ?? "").replace(/[\t ]+#+$/, "")),
      });
      index++;
      continue;
    }
    if (
      index + 1 < lines.length &&
      /^ {0,3}(?:=+|-+)[\t ]*$/.test(lines[index + 1])
    ) {
      blocks.push({
        kind: "heading",
        level: lines[index + 1].trimStart().startsWith("=") ? 1 : 2,
        children: inlineNodes(line.trim()),
      });
      index += 2;
      continue;
    }
    if (isThematicBreak(line)) {
      blocks.push({ kind: "rule" });
      index++;
      continue;
    }
    if (/^ {0,3}>/.test(line)) {
      const quoted: string[] = [];
      while (index < lines.length) {
        const quote = lines[index].match(/^ {0,3}>[\t ]?(.*)$/);
        if (quote) {
          quoted.push(quote[1]);
          index++;
        } else if (!lines[index].trim()) {
          quoted.push("");
          index++;
        } else break;
      }
      blocks.push({ kind: "quote", children: blockNodes(quoted, depth + 1) });
      continue;
    }
    const marker = listMarker(line);
    if (marker && marker.indent <= 3) {
      const list = parseList(lines, index, depth);
      blocks.push(list.block);
      index = list.nextIndex;
      continue;
    }
    const paragraph: string[] = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      (!paragraph.length || !startsBlock(lines[index]))
    ) {
      paragraph.push(lines[index]);
      index++;
    }
    blocks.push({
      kind: "paragraph",
      children: inlineNodes(paragraphText(paragraph)),
    });
  }
  return blocks;
}

function inlineContent(
  nodes: InlineNode[],
  onOpenLink: (url: string) => void,
  path: string,
): ComponentChildren {
  return nodes.map((node, index) => {
    const key = `${path}-${index}`;
    switch (node.kind) {
      case "text":
        return <Fragment key={key}>{node.value}</Fragment>;
      case "break":
        return <br key={key} />;
      case "code":
        return (
          <code
            key={key}
            class="rounded bg-secondaryContainer px-1 py-0.5 font-mono text-[.9em]"
          >
            {node.value}
          </code>
        );
      case "emphasis":
        return (
          <em key={key}>{inlineContent(node.children, onOpenLink, key)}</em>
        );
      case "strong":
        return (
          <strong key={key} class="font-semibold">
            {inlineContent(node.children, onOpenLink, key)}
          </strong>
        );
      case "link": {
        const url = node.url;
        return url ? (
          <button
            key={key}
            type="button"
            value={url}
            title={url}
            class="inline cursor-pointer border-0 bg-transparent p-0 text-left align-baseline font-inherit text-primary underline underline-offset-2"
            onClick={(event) => {
              event.stopPropagation();
              onOpenLink(event.currentTarget.value);
            }}
          >
            {inlineContent(node.children, onOpenLink, key)}
          </button>
        ) : (
          <Fragment key={key}>
            {inlineContent(node.children, onOpenLink, key)}
          </Fragment>
        );
      }
    }
  });
}

function heading(
  level: number,
  children: ComponentChildren,
  key: string,
): ComponentChildren {
  switch (level) {
    case 1:
      return (
        <h1 key={key} class="text-2xl font-semibold">
          {children}
        </h1>
      );
    case 2:
      return (
        <h2 key={key} class="text-xl font-semibold">
          {children}
        </h2>
      );
    case 3:
      return (
        <h3 key={key} class="text-lg font-semibold">
          {children}
        </h3>
      );
    case 4:
      return (
        <h4 key={key} class="font-semibold">
          {children}
        </h4>
      );
    case 5:
      return (
        <h5 key={key} class="font-semibold">
          {children}
        </h5>
      );
    default:
      return (
        <h6 key={key} class="font-semibold">
          {children}
        </h6>
      );
  }
}

function blockContent(
  blocks: BlockNode[],
  onOpenLink: (url: string) => void,
  path = "markdown",
): ComponentChildren {
  return blocks.map((block, index) => {
    const key = `${path}-${index}`;
    switch (block.kind) {
      case "heading":
        return heading(
          block.level,
          inlineContent(block.children, onOpenLink, key),
          key,
        );
      case "paragraph":
        return (
          <p key={key}>{inlineContent(block.children, onOpenLink, key)}</p>
        );
      case "rule":
        return <hr key={key} class="border-outlineVariant" />;
      case "quote":
        return (
          <blockquote
            key={key}
            class="space-y-3 border-l-4 border-outlineVariant pl-4 text-secondary"
          >
            {blockContent(block.children, onOpenLink, key)}
          </blockquote>
        );
      case "code":
        return (
          <pre
            key={key}
            class="overflow-x-auto rounded-lg bg-secondaryContainer p-3 font-mono text-xs leading-5"
          >
            <code>{block.value}</code>
          </pre>
        );
      case "list": {
        const items = block.items.map((item, itemIndex) => (
          <li key={`${key}-${itemIndex}`} class="space-y-2">
            {blockContent(item, onOpenLink, `${key}-${itemIndex}`)}
          </li>
        ));
        return block.ordered ? (
          <ol
            key={key}
            start={block.start === 1 ? undefined : block.start}
            class="list-decimal space-y-2 pl-6"
          >
            {items}
          </ol>
        ) : (
          <ul key={key} class="list-disc space-y-2 pl-6">
            {items}
          </ul>
        );
      }
    }
  });
}

export function isMarkdownContentType(
  contentType: string | undefined,
): boolean {
  return contentType?.split(";", 1)[0].trim().toLowerCase() === "text/markdown";
}

export function isSafeMarkdownSize(content: string): boolean {
  return (
    new TextEncoder().encode(content).byteLength <= MAX_SAFE_MARKDOWN_BYTES
  );
}

export function SafeMarkdown(props: {
  content: string;
  onOpenLink: (url: string) => void;
  tooLargeMessage: ComponentChildren;
}) {
  if (!isSafeMarkdownSize(props.content)) {
    return (
      <p role="alert" class="text-sm text-error">
        {props.tooLargeMessage}
      </p>
    );
  }
  const blocks = blockNodes(props.content.replaceAll("\r\n", "\n").split("\n"));
  return (
    <div class="space-y-4 text-sm leading-6">
      {blockContent(blocks, props.onOpenLink)}
    </div>
  );
}
